import PropTypes from 'prop-types';
import { useCallback, useState } from 'react';
import { twJoin } from 'tailwind-merge';

import TabNav from 'shopper/components/TabNav/TabNav';
import TabNavItem from 'shopper/components/TabNav/TabNavItem';

export const TAB_COMMENTS = 'comments';
export const TAB_REVIEWS = 'reviews';

const CommentsAndReviewsTabs = ({
  activeTab: controlledActiveTab,
  children,
  commentsCount = 0,
  commentsTabRightSlot = null,
  onTabChange,
  reviewsCount = 0,
  reviewsTabRightSlot = null,
}) => {
  const [uncontrolledActiveTab, setUncontrolledActiveTab] =
    useState(TAB_COMMENTS);
  const isControlled = controlledActiveTab !== undefined;
  const activeTab = isControlled ? controlledActiveTab : uncontrolledActiveTab;

  const handleTabActivate = useCallback(
    (next) => {
      if (next === activeTab) {
        return;
      }

      if (!isControlled) {
        setUncontrolledActiveTab(next);
      }
      if (onTabChange) {
        onTabChange(next);
      }
    },
    [activeTab, isControlled, onTabChange]
  );

  const toggleTab = useCallback(() => {
    handleTabActivate(activeTab === TAB_COMMENTS ? TAB_REVIEWS : TAB_COMMENTS);
  }, [activeTab, handleTabActivate]);

  const [commentsPanel, reviewsPanel] = Array.isArray(children)
    ? children
    : [children, null];

  const isCommentsActive = activeTab === TAB_COMMENTS;
  const isReviewsActive = activeTab === TAB_REVIEWS;

  // Padrão ARIA tabs: setas esquerda/direita alternam a aba ativa.
  const handleKeyDown = (event) => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      toggleTab();
    }
  };

  return (
    <div className="flex min-h-0 w-full flex-col lg:h-full">
      {/*
        Wrapper `relative` pra acomodar o `commentsTabRightSlot` (ex.:
        ícone de ordenação) absolutely-positioned dentro do espaço da
        aba ativa, sem quebrar a largura 50/50 das duas abas.

        O TabNav do Shopper não implementa ARIA nativamente. Os atributos
        (role, aria-selected, tabIndex) são repassados via spread props
        para o <nav> e para o elemento do TabNavItem.
      */}
      <div className="relative">
        <TabNav aria-label="Comentários e Avaliações" role="tablist">
          <TabNavItem
            active={isCommentsActive}
            aria-controls="offer-tabpanel-comments"
            aria-selected={isCommentsActive}
            className="flex-1 px-4"
            id="offer-tab-comments"
            role="tab"
            tabIndex={isCommentsActive ? 0 : -1}
            onClick={() => handleTabActivate(TAB_COMMENTS)}
            onKeyDown={handleKeyDown}
          >
            Comentários ({commentsCount})
          </TabNavItem>
          <TabNavItem
            active={isReviewsActive}
            aria-controls="offer-tabpanel-reviews"
            aria-selected={isReviewsActive}
            className="flex-1 px-4"
            id="offer-tab-reviews"
            role="tab"
            tabIndex={isReviewsActive ? 0 : -1}
            onClick={() => handleTabActivate(TAB_REVIEWS)}
            onKeyDown={handleKeyDown}
          >
            Avaliações ({reviewsCount})
          </TabNavItem>
        </TabNav>
        {/*
          O slot da aba de comentários (ícone de ordenação) fica no
          canto direito da metade ESQUERDA do TabNav, ou seja, dentro
          da área visual da aba "Comentários". `right-[calc(50%+0.5rem)]`
          ancora 0.5rem à esquerda do divisor das duas abas.

          Escondido no mobile (`hidden lg:block`) — em larguras
          pequenas o contador "Comentários (N)" fica próximo do meio
          do TabNav e o ícone acaba sobrepondo o texto. Em telas lg+
          a aba tem espaço sobrando à direita do label.
        */}
        {isCommentsActive && commentsTabRightSlot && (
          <div className="absolute right-[calc(50%+0.5rem)] top-1/2 hidden -translate-y-1/2 lg:block">
            {commentsTabRightSlot}
          </div>
        )}
        {isReviewsActive && reviewsTabRightSlot && (
          <div className="absolute right-2 top-1/2 hidden -translate-y-1/2 lg:block">
            {reviewsTabRightSlot}
          </div>
        )}
      </div>
      {/*
        Os dois painéis vivem no DOM desde o SSR para que o conteúdo dos
        reviews chegue no HTML inicial (requisito de SEO — ver techspec,
        "Riscos: reviews em aba oculta podem não ser indexados"). A aba
        inativa é escondida via `display: none` (classe `hidden`), o que
        também a remove da árvore de acessibilidade — sem necessidade de
        `aria-hidden` ou `inert` complementar.
      */}
      <div
        aria-labelledby="offer-tab-comments"
        className={twJoin(
          'min-h-0 flex-1 flex-col',
          isCommentsActive ? 'flex' : 'hidden'
        )}
        id="offer-tabpanel-comments"
        role="tabpanel"
      >
        {commentsPanel}
      </div>
      <div
        aria-labelledby="offer-tab-reviews"
        className={twJoin(
          'min-h-0 flex-1 flex-col',
          isReviewsActive ? 'flex' : 'hidden'
        )}
        id="offer-tabpanel-reviews"
        role="tabpanel"
      >
        {reviewsPanel}
      </div>
    </div>
  );
};

CommentsAndReviewsTabs.propTypes = {
  /**
   * Controla a aba ativa externamente. Quando definido, o componente
   * opera como controlled. Omita para uncontrolled.
   */
  activeTab: PropTypes.oneOf([TAB_COMMENTS, TAB_REVIEWS]),
  /**
   * Array com dois elementos: [painelComentarios, painelAvaliacoes].
   * Ambos são renderizados no DOM; a aba inativa é escondida via CSS
   * para garantir SSR dos reviews (SEO).
   */
  children: PropTypes.node.isRequired,
  /**
   * Contador de comentários exibido ao lado do rótulo da aba.
   * Só aparece quando > 0.
   */
  commentsCount: PropTypes.number,
  /**
   * Conteúdo renderizado à direita do TabNav (absoluto) APENAS
   * quando a aba de comentários está ativa. Usado pra encaixar o
   * ícone de ordenação sem ocupar uma linha separada abaixo.
   */
  commentsTabRightSlot: PropTypes.node,
  /**
   * Callback chamado quando o usuário alterna a aba ativa. Recebe o
   * novo valor (`'comments'` ou `'reviews'`).
   */
  onTabChange: PropTypes.func,
  /**
   * Contador de avaliações exibido ao lado do rótulo da aba.
   * Só aparece quando > 0.
   */
  reviewsCount: PropTypes.number,
  /**
   * Análogo ao `commentsTabRightSlot`, porém só é renderizado quando
   * a aba de avaliações está ativa.
   */
  reviewsTabRightSlot: PropTypes.node,
};

export default CommentsAndReviewsTabs;
